import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Heading } from "@fronterahealth/frontera-ui-components";

import { type CreateOrganizationInput, useBasecampOrganizationsCreateCreate } from "@api/rest/types-and-hooks";
import { FormContainer } from "@components/forms/FormLayout";
import { OrganizationFormFields } from "@components/forms/OrganizationFormFields";
import { useFormUtils } from "@components/forms/useFormUtils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { EhrSyncField } from "@pages/Organizations/EhrSyncField";
import { EhrSyncMode, ORG_CONNECTION_TYPES } from "@pages/Organizations/constants";
import { EHR_SYNC_COPY, getEhrSyncMode, resolveEhrSyncEnabled, takesVerticalOrgId } from "@pages/Organizations/ehrSync";

export const OrganizationNew: React.FC = () => {
  const navigate = useNavigate();

  const { mutateAsync } = useBasecampOrganizationsCreateCreate();

  const formUtils = useFormUtils<CreateOrganizationInput>({
    mutationFn: async (data) => {
      // Store only what Ops chose. Passage is on whatever is stored, so it's
      // sent as off: an org that later leaves Passage then starts gated.
      const mode = getEhrSyncMode(data.organization_type);
      const storedEhrSync = mode === EhrSyncMode.EDITABLE && !!data.ehr_sync_enabled;
      // A vertical id typed before switching away from Frontera EHR isn't sent.
      const externalId = takesVerticalOrgId(data.organization_type) ? data.external_id?.trim() || null : null;
      const response = await mutateAsync({
        data: { ...data, ehr_sync_enabled: storedEhrSync, external_id: externalId },
      });

      if (response.status !== 200) {
        notifyError(response.data.detail || "Failed to create organization");
        return;
      }
      notifySuccess(resolveEhrSyncEnabled(mode, storedEhrSync) ? EHR_SYNC_COPY.createdWithSync : EHR_SYNC_COPY.created);
      navigate("/organizations");
    },
    defaultValues: {
      name: "",
      display_name: "",
      organization_type: undefined,
      hubspot_company_id: "",
      logo_url: "",
      primary_color: "",
      background_color: "",
      connection_types: ORG_CONNECTION_TYPES,
      ehr_sync_enabled: false,
      external_id: "",
    },
  });
  const { onSubmit, formState, watch, setValue } = formUtils;

  const ehrSyncMode = getEhrSyncMode(watch("organization_type"));
  const ehrSyncEnabled = watch("ehr_sync_enabled") ?? false;

  // Passage shows the box checked without storing it, so leaving Passage can
  // flip it to off; say so. Subscribing (rather than an effect on the watched
  // value) keeps the notice tied to the user's type change.
  const [ehrSyncResetNotice, setEhrSyncResetNotice] = useState<string | null>(null);
  const previousEhrSyncModeRef = useRef<EhrSyncMode>(EhrSyncMode.UNSELECTED);
  useEffect(() => {
    const subscription = watch((values, { name }) => {
      if (name !== "organization_type") return;
      const previousMode = previousEhrSyncModeRef.current;
      const mode = getEhrSyncMode(values.organization_type);
      previousEhrSyncModeRef.current = mode;
      const turnedOff =
        previousMode === EhrSyncMode.ALWAYS_ON &&
        mode !== EhrSyncMode.ALWAYS_ON &&
        !resolveEhrSyncEnabled(mode, !!values.ehr_sync_enabled);
      setEhrSyncResetNotice(turnedOff ? EHR_SYNC_COPY.resetNotice : null);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  return (
    <div className="flex flex-col min-h-full py-8 items-center bg-bg-default px-4 sm:px-6 lg:px-8">
      {/* Page header */}
      <div className="w-full max-w-[900px] mb-4">
        <div className="flex items-center gap-2 mb-2">
          <button
            type="button"
            onClick={() => navigate("/organizations")}
            className="text-sm text-text-secondary hover:text-text-primary transition-colors"
          >
            Organizations
          </button>
          <ChevronRightIcon className="h-3 w-3 text-text-tertiary" />
          <span className="text-sm text-text-secondary">Create new organization</span>
        </div>
        <Heading size="medium">Create new organization</Heading>
      </div>

      {/* Form card */}
      <div className="w-full max-w-[900px] bg-bg-surface border border-border-strong rounded-2xl px-4 sm:px-8 pt-6 pb-8">
        <FormContainer onSubmit={onSubmit}>
          <OrganizationFormFields
            formUtils={formUtils}
            showVerticalOrgId
            organizationTypeAddon={
              <EhrSyncField
                mode={ehrSyncMode}
                checked={ehrSyncEnabled}
                onChange={(checked) => {
                  setValue("ehr_sync_enabled", checked);
                  setEhrSyncResetNotice(null);
                }}
                resetNotice={ehrSyncResetNotice}
              />
            }
          />

          {/* Action buttons */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="primary"
              buttonStyle="border"
              size="md"
              text="Cancel"
              onClick={() => navigate("/organizations")}
            />
            <Button
              type="submit"
              variant="primary"
              buttonStyle="filled"
              size="md"
              text={formState.isSubmitting ? "Submitting..." : "Submit"}
              disabled={formState.isSubmitting}
            />
          </div>
        </FormContainer>
      </div>
    </div>
  );
};
